import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useEnhancedToast } from './use-enhanced-toast';

export interface UserProfile {
  id: string;
  email: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  bio?: string;
  reading_preferences: Record<string, any>;
  notification_settings: {
    email: boolean;
    push: boolean;
  };
  theme_preference: 'light' | 'dark' | 'auto';
  language: string;
  timezone: string;
  created_at: string;
  updated_at: string;
  last_active: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: 'admin' | 'moderator' | 'user' | 'premium';
  granted_by?: string;
  granted_at: string;
}

/**
 * One profile+role fetch per user, shared by every useProfile() instance.
 *
 * The hook holds state per component and is mounted by seven of them (Header,
 * Protagonists, AuthorPopup, ChronoTimeline, …), so a signed-in page load sent
 * /profiles 10x and /user_roles 9x inside the same 20 ms. Instances now share
 * the in-flight request and its result for a short window. Each instance still
 * commits the result to its own state exactly as before, so `loading` and
 * `hasRole` behave identically — ProtectedAdminRoute depends on that.
 */
const SHARED_TTL_MS = 30_000;
let shared: { userId: string; at: number; promise: Promise<[UserProfile | null, UserRole | null]> } | null = null;

function invalidateSharedProfile() {
  shared = null;
}

async function fetchProfileFor(userId: string): Promise<UserProfile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    if (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
    return (data as UserProfile) ?? null;
  } catch (error) {
    console.error('Error fetching profile:', error);
    return null;
  }
}

async function fetchUserRoleFor(userId: string): Promise<UserRole | null> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', userId)
      .order('granted_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
    return (data as UserRole) ?? null;
  } catch (error) {
    console.error('Error fetching user role:', error);
    return null;
  }
}

function loadSharedProfile(userId: string) {
  const fresh = shared && shared.userId === userId && Date.now() - shared.at < SHARED_TTL_MS;
  if (!fresh) {
    shared = {
      userId,
      at: Date.now(),
      promise: Promise.all([fetchProfileFor(userId), fetchUserRoleFor(userId)]),
    };
  }
  return shared!.promise;
}

export const useProfile = () => {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useEnhancedToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [dataLoading, setDataLoading] = useState(false);

  // Combined loading: true if auth is loading OR if we have a user and role data is not ready yet
  // NOTE: we must treat "userRole === null" as loading even if dataLoading flipped false in the same render,
  // otherwise protected routes can redirect before role state commits.
  const loading = authLoading || (!!user && (dataLoading || userRole === null));
  useEffect(() => {
    const loadUserData = async () => {
      if (authLoading) {
        // Wait for auth to finish
        return;
      }
      
      if (!user) {
        setProfile(null);
        setUserRole(null);
        setDataLoading(false);
        return;
      }

      setDataLoading(true);
      try {
        // Fetch both profile and role in parallel, then commit state together
        const [nextProfile, nextRole] = await loadSharedProfile(user.id);
        console.log('Profile/Role loaded:', { profile: !!nextProfile, role: nextRole?.role });
        setProfile(nextProfile);
        setUserRole(nextRole);
      } finally {
        setDataLoading(false);
      }
    };

    loadUserData();

    // Set up real-time subscription for profile updates
    // IMPORTANT: channel names must be unique per hook instance to avoid
    // "subscribe can only be called a single time per channel instance" crashes
    // when multiple components use useProfile at the same time.
    if (user) {
      const channelName = `profile-changes:${user.id}:${Math.random().toString(36).slice(2, 8)}`;

      const profileChannel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${user.id}`
          },
          (payload) => {
            console.log('Profile updated:', payload);
            if (payload.eventType === 'UPDATE') {
              invalidateSharedProfile();
              setProfile(payload.new as UserProfile);
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(profileChannel);
      };
    }
  }, [user, authLoading]);

  const fetchProfile = async (): Promise<UserProfile | null> =>
    user ? fetchProfileFor(user.id) : null;

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user || !profile) return false;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        toast({
          title: "Error updating profile",
          description: error.message,
          variant: "destructive"
        });
        return false;
      }

      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
        variant: "success"
      });

      // Optimistically update local state
      invalidateSharedProfile();
      setProfile(prev => prev ? { ...prev, ...updates } : null);
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error updating profile",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
      return false;
    }
  };

  const updateLastActive = async () => {
    if (!user) return;

    try {
      await supabase
        .from('profiles')
        .update({ last_active: new Date().toISOString() })
        .eq('id', user.id);
    } catch (error) {
      console.error('Error updating last active:', error);
    }
  };

  const hasRole = useCallback((role: 'admin' | 'moderator' | 'user' | 'premium'): boolean => {
    if (!userRole) return false;

    // Admin has access to everything
    if (userRole.role === 'admin') return true;

    // Check for specific role or higher
    const roleHierarchy = { admin: 4, moderator: 3, premium: 2, user: 1 } as const;
    const userLevel = roleHierarchy[userRole.role];
    const requiredLevel = roleHierarchy[role];

    return userLevel >= requiredLevel;
  }, [userRole]);

  return {
    profile,
    userRole,
    loading,
    updateProfile,
    updateLastActive,
    hasRole,
    refetch: async () => {
      invalidateSharedProfile();
      const next = await fetchProfile();
      setProfile(next);
      return next;
    }
  };
};